'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
}

interface ServiceContent {
  sectionTitle: string;
  sectionDescription: string;
  services: Service[];
  ctaText: string;
  ctaLink: string;
}

interface ServiceOverviewProps {
  className?: string;
}

const ServiceOverview = ({ className = '' }: ServiceOverviewProps) => {
  const [content, setContent] = useState<ServiceContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('content_pages')
        .select('content')
        .eq('slug', 'homepage_services')
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      
      if (data?.content) {
        const parsedContent = typeof data.content === 'string' 
          ? JSON.parse(data.content) 
          : data.content;
        setContent(parsedContent);
      }
    } catch (error) {
      console.error('Error loading service content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !content) {
    return (
      <section className={`bg-white py-20 lg:py-28 ${className}`}>
        <div className="mx-auto px-6 lg:px-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-2/3 mx-auto"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-white py-20 lg:py-28 ${className}`}>
      <div className="mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-5xl text-primary mb-6">
            {content.sectionTitle}
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            {content.sectionDescription}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {content.services.map((service, index) => (
            <div
              key={index}
              className="bg-surface border border-border rounded-sm p-8 hover:shadow-institutional transition-shadow duration-smooth"
            >
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mb-6">
                <Icon
                  name={service.icon as any}
                  size={28}
                  variant="solid"
                  className="text-accent"
                />
              </div>

              <h3 className="font-headline font-semibold text-xl text-primary mb-4">
                {service.title}
              </h3>

              <p className="font-body text-base text-text-secondary mb-6 leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-3">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <Icon
                      name="CheckIcon"
                      size={20}
                      variant="solid"
                      className="text-success mt-0.5 flex-shrink-0"
                    />
                    <span className="font-body text-sm text-text-primary">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            href={content.ctaLink}
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground font-headline font-semibold text-base rounded-sm hover:bg-secondary transition-colors duration-smooth shadow-institutional"
          >
            <span>{content.ctaText}</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceOverview;